import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import { CLUSTERS, type ClusterId, type Vec3 } from '../data/content';
import { CLOUD_WORDS, type WordInfo } from '../lib/model';
import { CLOUD_CENTER } from '../lib/layout';
import { useLabelFactor } from './sceneHooks';
type Props = {
  hiddenWords: Set<string>;
  neighbors: WordInfo[];
  focus: {
    pos: Vec3;
    color: string;
  } | null;
  focusCluster: ClusterId | null;
  reducedMotion: boolean;
};
const DOT_GEO = new THREE.SphereGeometry(0.06, 12, 12);
const DOT_COLOR = new THREE.Color('#4A5068');
const NEAR_COLOR = new THREE.Color('#EDEEF2');
export function MeaningCloud({
  hiddenWords,
  neighbors,
  focus,
  focusCluster,
  reducedMotion
}: Props) {
  const groupRef = useRef<THREE.Group>(null);
  const labelFactor = useLabelFactor();
  const near = new Set(neighbors.map(n => n.norm));
  const focusLocal: Vec3 | null = focus ? [focus.pos[0] - CLOUD_CENTER[0], focus.pos[1] - CLOUD_CENTER[1], focus.pos[2] - CLOUD_CENTER[2]] : null;
  useFrame((_, delta) => {
    const g = groupRef.current;
    if (!g) return;
    const s = THREE.MathUtils.damp(g.scale.x, 1, reducedMotion ? 30 : 3, Math.min(delta, 0.05));
    g.scale.setScalar(s);
  });
  return <group ref={groupRef} position={CLOUD_CENTER} scale={0.8} data-id="element-46" data-mp="c9rite3t0qmm">
      {CLOUD_WORDS.filter(w => !hiddenWords.has(w.norm)).map(w => {
      const isNear = near.has(w.norm);
      return <group key={w.norm} position={w.pos} data-id="element-47" data-mp="13yumosrs3yyq">
            <mesh geometry={DOT_GEO} raycast={() => null} data-id="element-48" data-mp="q8r3kao74ldi">
              <meshBasicMaterial color={isNear ? NEAR_COLOR : DOT_COLOR} data-id="element-49" data-mp="q87vrms9fdd7" />
            </mesh>
            <Html center position={[0, 0.22, 0]} distanceFactor={10 * labelFactor} zIndexRange={[8, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-50" data-mp="v69fm9xifgpq">
              <span className={`select-none whitespace-nowrap font-mono text-[14px] animate-[fadeIn_0.8s_ease_both] ${isNear ? 'text-[#EDEEF2]' : 'text-[#6B7089]'}`} data-id="element-51" data-mp="1xkdjea33mla0">
                {w.display}
              </span>
            </Html>
          </group>;
    })}

      {CLUSTERS.map(c => <Html key={c.id} center position={[c.center[0], c.center[1] + 1.85, c.center[2]]} distanceFactor={11 * labelFactor} zIndexRange={[8, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-52" data-mp="1ip7fptrh6yju">
          <span className={`select-none whitespace-nowrap text-[12px] font-semibold uppercase tracking-[0.12em] animate-[fadeIn_1s_ease_both] ${c.id === focusCluster ? 'text-[#C9CCD8]' : 'text-[#4E5366]'}`} data-id="element-53" data-mp="14t3ma2ofeu8f">
            {c.label}
          </span>
        </Html>)}

      {focus && focusLocal && neighbors.map(n => <Line key={n.norm} points={[focusLocal, n.pos]} color={focus.color} lineWidth={1.2} transparent opacity={0.55} data-id="element-54" data-mp="32l3g2f0fu3l0" />)}
    </group>;
}